import { html, render, RootPart } from 'lit';
import { Constructor } from 'lit-element';
import { GridColumnElement } from '@vaadin/vaadin-grid/vaadin-grid-column';
import { GzDataTableWebComponent } from '../component/GzDataTableWebComponent';
import { DataTableEvents, IDataTableColumn } from '../types';
import { ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../gzButton/types';
import { isEmpty } from '@nirvana/shared/basic-types/object';

// all protected attributes and methods from the decorator class, used by other decorators, should be exposed here
export interface TableFooterProtectedInterface<TDataTableItem = unknown> {
  renderFooter(
    root: HTMLElement,
    buttonText: string,
    action: Function,
    additionalClasses?: string,
    disabled?: boolean
  ): RootPart | undefined;
  shouldRenderEmptyStateFooter(columnIndex: number): boolean;
  onEmptyStateFooterButtonClick(): void;
}

export const tableFooter = <T extends Constructor<GzDataTableWebComponent<TDataTableItem>>, TDataTableItem = unknown>(
  dataTableClass: T
) => {
  class TableFooterDecorator extends dataTableClass {
    protected renderColumn(column: IDataTableColumn, index: number): GridColumnElement {
      // overridden function
      const columnElement = super.renderColumn(column, index);
      if (this.shouldRenderEmptyStateFooter(index)) {
        columnElement.footerRenderer = root =>
          this.renderFooter(root, this.strings.emptyStateFooterButton, this.onEmptyStateFooterButtonClick);
      }
      return columnElement;
    }

    protected renderFooter(
      root: HTMLElement,
      buttonText: string,
      action: Function,
      additionalClasses = '',
      disabled = false
    ): RootPart | undefined {
      const classes = `footer ${additionalClasses}`;
      return render(
        html` <div class="${classes}">
          <gz-button
            type="${ButtonTypes.QUIET}"
            buttonHeight="${ButtonHeights.SMALL}"
            buttonFontSize="${ButtonFontSizes.SMALL}"
            lowercase
            @click="${action}"
            ?disabled="${disabled}"
          >
            ${buttonText}
          </gz-button>
        </div>`,
        root
      );
    }

    protected shouldRenderEmptyStateFooter(columnIndex: number): boolean {
      return columnIndex === 0 && isEmpty(this.currentItems) && this.strings.emptyStateFooterButton !== '';
    }

    protected onEmptyStateFooterButtonClick(): void {
      this.dispatchEvent(
        new CustomEvent(DataTableEvents.EMPTY_STATE_FOOTER_BUTTON_CLICKED, { bubbles: true, composed: true })
      );
    }
  }

  return TableFooterDecorator;
};
